"use client";
import Image from "next/image";
import React, { useEffect, useMemo } from "react";
import { CiCircleCheck } from "react-icons/ci";
import { useCustomImageContext } from "../../contexts/CustomImageContext";
import { useCarouselAPIContext } from "@/contexts/CarouselAPIContext";

type Props = {
  small?: boolean;
};

export default function MaskSelector(props: Props) {
  const [value, setValue] = React.useState<number>(0);
  const [imgContext, setImgContext] = useCustomImageContext();
  const [carouselAPI, _] = useCarouselAPIContext()

  const selectMask = (src: string | null) => {
    if (carouselAPI?.selectedScrollSnap() === 0) {
      setImgContext((prev) => {
        return { ...prev, frontMask: src };
      });
    } else if (carouselAPI?.selectedScrollSnap() === 1) {
      setImgContext((prev) => {
        return { ...prev, backMask: src };
      });
    }
  };

  useEffect(() => {
    if (!carouselAPI) return

    const onSelect = () => {
      setValue(carouselAPI.selectedScrollSnap())
    }

    carouselAPI?.on("select", onSelect)

    return () => {
      carouselAPI.off("select", onSelect)
    }

  }, [carouselAPI])

  const selected = useMemo(() => {
    if (value == 1) {
      return imgContext.backMask
    }
    else
      return imgContext.frontMask

  }, [imgContext.frontMask, imgContext.backMask, value])


  if (imgContext.frontImages.length + imgContext.backImages.length === 0) {
    return <></>;
  }

  return (
    <div className="my-2 mb-5 w-full px-0 md:px-2">
      <h2 className="mb-4 text-sm md:text-base font-semibold text-slate-800 dark:text-white">
        Select Image Shape
      </h2>
      <div className="w-full md:w-fit">
        <div className="my-2 flex w-full flex-row gap-4 overflow-scroll noscrollbar">
          <MaskItem selected={selected} setSelected={selectMask} name={"Default Square"} />
          <MaskItem src="/mask.png" selected={selected} setSelected={selectMask} name={"Diffusion"} />
          <MaskItem src="/mask2.png" selected={selected} setSelected={selectMask} name={"Paint Splash"} />
          <MaskItem src="/mask3.png" selected={selected} setSelected={selectMask} name={"Paint Brush"} />
          <MaskItem src="/mask4.png" selected={selected} setSelected={selectMask} name={"No Corner"} />
        </div>
      </div>
    </div>
  );
}

const MaskItem = (props: {
  src?: string;
  selected: string | null;
  setSelected: (src: string | null) => void;
  name: string;
}) => {
  const handleClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (window.innerWidth < 500) window.scrollTo(0, 0);

    if (props.src) props.setSelected(props.src);
    else props.setSelected(null);
  };

  return (

    <div
      className="group aspect-square w-16 rounded-md border bg-black border-ffmain-800 p-0 sm:w-20"
      data-tooltip-content={props.name}
      data-tooltip-id="tooltip"
    >
      <div className="relative size-full">
        {(props.selected === props.src || (props.selected === null && !props.src)) && (
          <div className="pointer-events-none absolute flex size-full items-center justify-center bg-black bg-opacity-30 text-green-200">
            <CiCircleCheck className=" pointer-events-none z-20 size-6 sm:size-10" />
          </div>
        )}
        {props.src ? (
          <Image
            src={props.src}
            alt={`Generated image`}
            className={`size-full cursor-pointer rounded-md bg-white mix-blend-lighten object-cover group-hover:scale-105 group-hover:bg-slate-200 transition-all duration-300 shape-selector`}
            data-track="Mask Selector"
            onClick={handleClick}
            priority
            loading="eager"
            width={100}
            height={100}
          />
        ) : (
          <div
            className="size-full cursor-pointer rounded-md bg-white object-cover transition group-hover:scale-105 group-hover:bg-slate-300"
            onClick={handleClick}
          ></div>
        )}
      </div>
    </div>
  );
};
